import { KeyboardEventHandler, PropsWithChildren, ReactNode } from 'react';
import { Button, Dialog } from '@mui/material';
import { DraggablePaper, SlideTransition } from '../../Infrastructure/Theme/AppThemeComponents';
import ModalAnnouncementCenter from './ModalAnnouncementCenter';
import { useModalProvider } from './ModalContext';
import { useAppDispatch } from '@reduxStore';
import { removeModal } from './modalSlice';

import styles from './ApplicationModal.module.css';

interface ApplicationModalProps {
	/** Flag whether the modal is open or closed */
	isOpen: boolean;
	/** Handler for closing the modal */
	onClose: () => void;
	/** Text to display on the modal title bar */
	title: string;
	/** Button actions to render on the bar */
	actions?: ReactNode;
	/** Override the default close button text */
	closeButtonText?: string;
	/** Handler called once the modal has finished closing and been removed from the modal queue */
	onExited?: () => void;
	/** Keydown handler for the whole dialog */
	onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

/**
 * Dialog wrapper to implement Application preferences and behavior.
 * Children component should be wrapped in a MUI <DialogContent> component.
 * Action buttons should be wrapped in a MUI <DialogActions> component.
 */
export default function ApplicationModal(props: PropsWithChildren & ApplicationModalProps) {
	const { isOpen, onClose, onExited } = props;
	const modalContext = useModalProvider();
	const appDispatch = useAppDispatch();

	/** Handler event for closing the modal */
	const handleClose = (_: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
		if (reason && reason === 'backdropClick') {
			return;
		}

		onClose();
	};

	const handleTransitionExit = () => {
		appDispatch(removeModal());
		modalContext.reset();
		onExited?.();
	};

	return (
		<Dialog
			open={isOpen}
			onClose={handleClose}
			slots={{
				paper: DraggablePaper,
				transition: SlideTransition
			}}
			slotProps={{
				paper: { className: styles.paper }
			}}
			onTransitionExited={handleTransitionExit}
			onKeyDown={props.onKeyDown}
			aria-labelledby={'draggable-window'}
		>
			<div
				id={'draggable-window'}
				className={styles.header}
			>
				{props.title}
				<div className={styles['actions']}>
					{props.actions && props.actions}
					<Button
						variant='outlined'
						sx={{ marginRight: '4px' }}
						onClick={() => {
							onClose();
						}}
					>
						{props.closeButtonText ? props.closeButtonText : 'Close'}
					</Button>
				</div>
			</div>

			<ModalAnnouncementCenter />

			{props.children}
		</Dialog>
	);
}
